import { isEmpty, split } from "lodash";
import { useRouter } from "next/router";
import { classNames } from "primereact/utils";
import { useCompanies } from "../../../../common/api/useCompanies";
import usePermissions from "../../../../common/api/usePermissions";
import { Button } from "../../../../common/components";
import { PermissionsGate } from "../../../../common/components/PermissionsGate";
import {
    ADMIN_ROUTES,
    COMPANY_ROUTES,
    PERMISSIONS,
} from "../../../../common/constants";
import { useRedirectStore } from "../../../../common/store/useRedirectStore";
import { useSetUpSpacesStore } from "../../../set-up-spaces-flow/store";
import { ActiveModeChip } from "../ActiveModeChip";
import { AddLocationButton } from "../AddLocationButton";
import { LocationSelection } from "../LocationSelection";
import { UserMenu } from "../UserMenu";
import { AdminChip } from "./AdminChip";

interface HeaderProps {
    onOpenMenuClick?: () => void;
    showMenuIcon?: boolean;
    onLogoutClick?: () => void;
}

const Header = (props: HeaderProps) => {
    const {
        onOpenMenuClick = () => {},
        showMenuIcon = false,
        onLogoutClick = () => {},
    } = props;

    const router = useRouter();
    const { companyId, locationId } = router.query as {
        companyId?: string;
        locationId?: string;
    };

    const { lastLocationPath, setLastLocationPath } = useRedirectStore();

    const { toggleVisibility } = useSetUpSpacesStore();

    const { hasPermissions } = usePermissions();
    const { companies, isLoadingCompanies } = useCompanies();
    const hasCompany = isLoadingCompanies || companies?.[0];
    const showActiveModeChip =
        !!locationId &&
        hasPermissions({
            permissions: [PERMISSIONS.ENERGY_MODE.READ],
            companyId,
            locationId,
        });

    const handleSwitchBetterspaceView = (checked: boolean) => {
        if (checked) {
            // Remove URL parameter before saving
            const splitRoute = split(router.asPath, "?");
            splitRoute?.[0] && setLastLocationPath(splitRoute?.[0]);
            router.push(ADMIN_ROUTES.SYSTEMCHECK);
            return;
        }
        if (!isEmpty(companies)) {
            if (lastLocationPath) {
                router.push({
                    pathname: lastLocationPath,
                });
                return;
            }
            // Until Company Dashboard page will be added
            const defaultCompanyRoute = COMPANY_ROUTES.LOCATIONS;
            router.push({
                pathname: defaultCompanyRoute,
                query: {
                    companyId: companies[0].id,
                },
            });
        }
    };

    return (
        <div
            className={classNames(
                "z-10 h-12.5 shrink-0 px-6 flex items-center justify-end transition-colors duration-300",
                companyId && !locationId
                    ? "bg-tertiary-600"
                    : "bg-primary-500"
            )}
        >
            <div className="flex items-center">
                {showMenuIcon && (
                    <Button
                        className="md:hidden text-primary-text focus:shadow-none focus-visible:bg-surface-500/15"
                        icon="pi pi-bars"
                        rounded
                        text
                        aria-label="Open Sidebar"
                        onClick={onOpenMenuClick}
                    />
                )}
            </div>
            <div className="flex grow items-center justify-end gap-1.5">
                {showActiveModeChip && <ActiveModeChip className="mr-1.5" />}
                {/* Hide companies dropdown in Betterspace Admin view */}
                {(hasPermissions() && !companyId) || !hasCompany ? null : (
                    <LocationSelection
                        companies={companies}
                        isLoadingCompanies={isLoadingCompanies}
                    />
                )}

                {!hasCompany && !hasPermissions() && (
                    <button onClick={toggleVisibility}>
                        <i className="pi pi-plus-circle text-lg text-primary-text"/>
                    </button>
                )}
                {/* Add Company/Location button and Betterspace Admin switch are hidden in mobile view */}
                <div className="flex items-center gap-1.5">
                    <AddLocationButton />
                    <PermissionsGate>
                        <AdminChip
                            isChecked={!companyId}
                            onToggle={handleSwitchBetterspaceView}
                        />
                    </PermissionsGate>
                </div>

                <UserMenu onLogoutClick={onLogoutClick} />
            </div>
        </div>
    );
};

export default Header;
